import clsx from 'clsx';
import { forwardRef, ReactNode } from 'react';

type ButtonVariant = 'primary' | 'secondary' | 'third' | 'text';
type ButtonSize = 'sm' | 'md' | 'md-responsive' | 'lg-responsive' | 'lg';

type BaseProps = {
  variant?: ButtonVariant;
  size?: ButtonSize;
  fullWidth?: boolean;
  rounded?: boolean;
};

interface ButtonProps
  extends
  React.DetailedHTMLProps<React.ButtonHTMLAttributes<HTMLButtonElement>, HTMLButtonElement>,
  BaseProps {
  loading?: boolean;
  icon?: ReactNode;
  leftIcon?: ReactNode;
  rightIcon?: ReactNode;
}

const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      variant = 'primary',
      size = 'md', // Default size
      children,
      className,
      loading,
      disabled,
      rounded,
      fullWidth = false,
      icon,
      leftIcon,
      rightIcon,
      ...props
    },
    ref
  ) => {
    const baseClasses = `
      relative font-medium flex items-center justify-center group
      transition-all duration-300 gap-1 disabled:opacity-50
      focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2
      cursor-pointer 
      disabled:pointer-events-none
    `;

    // Size-specific classes (padding, text size, maybe line height)
    const sizeClasses = clsx({
      'px-2 h-[36px]  text-sm leading-5': size === 'sm',
      'px-4 h-[44px]  text-sm leading-5': size === 'md',
      'px-4 h-[36px] 2xl:h-[44px]  text-sm leading-5': size === 'md-responsive',
      ' h-[52px]  px-[30px] text-base leading-6': size === 'lg',
    });

    // Variant-specific classes (background, text color, border)
    const variantClasses = clsx({
      'bg-primary-500 text-white hover:bg-primary-400 active:bg-primary-300 text-white  focus-visible:outline-primary-500':
        variant === 'primary' && !disabled && !loading,
      'bg-white text-primary-900 border border-primary-500 hover:bg-primary-20 hover:border-primary-400 active:border-primary-200 focus-visible:outline-primary-500':
        variant === 'secondary' && !disabled && !loading,
      'bg-white text-primary-900 border border-gray-300 hover:border-primary-200 active:bg-primary-20  active:border-primary-100 focus-visible:outline-primary-300':
        variant === 'third' && !disabled && !loading,
      'bg-transparent text-primary-900 border-0 hover:text-primary-500 hover:bg-primary-20 focus-visible:outline-gray-400':
        variant === 'text' && !disabled && !loading,
      // Disabled states for variants (optional but good for clarity)
      'bg-gray-300 text-gray-200': variant === 'primary' && (disabled || loading),
      'bg-gray-100 text-gray-400 border border-gray-300':
        variant === 'secondary' && (disabled || loading),
      'bg-white text-gray-400 border border-gray-300': variant === 'third' && (disabled || loading),
      'bg-transparent text-gray-400 border-0': variant === 'text' && (disabled || loading),
    });

    // Rounded classes
    const roundedClasses = rounded ? 'rounded-full' : 'rounded-[8px]';

    // Loading state (disables interaction and can visually change)
    const loadingClasses = loading ? 'cursor-wait' : ''; // pointer-events-none is covered by disabled

    // Full width class
    const fullWidthClasses = fullWidth ? 'w-full' : '';

    // Combine all classes
    const combinedClassName = clsx(
      baseClasses,
      sizeClasses,
      variantClasses,
      roundedClasses,
      loadingClasses,
      fullWidthClasses,
      className // Allow overriding via className prop
    );

    // Icon variant-specific classes (text color for icons)
    const iconVariantClasses = clsx({
      'text-primary-50 group-hover:text-primary-50 group-active:text-primary-50':
        variant === 'primary' && !disabled && !loading,
      'text-gray-200': variant === 'primary' && (disabled || loading),
      'text-primary-900 group-hover:text-primary-400 group-active:text-primary-300':
        variant === 'secondary' && !disabled && !loading,
      'text-gray-400':
        (variant === 'secondary' || variant === 'third' || variant === 'text') &&
        (disabled || loading),
      'text-primary-900 group-hover:text-primary-500 group-active:text-primary-300':
        variant === 'third' && !disabled && !loading,
      'text-primary-900 group-hover:text-primary-500 group-active:text-primary-500':
        variant === 'text' && !disabled && !loading,
    });

    // Define icon wrapper styles (adjust size if needed per button size)
    const iconWrapperClasses = clsx('shrink-0', iconVariantClasses, {
      'h-[36px] grid place-items-center  ': size === 'sm',
      'h-[44px] grid place-items-center  ': size === 'md',
      'h-[36px] 2xl:h-[44px] grid place-items-center': size === 'md-responsive',
      'h-[36px] 2xl:h-[52px]  grid place-items-center': size === 'lg-responsive',
      'h-[52px grd place-items-center': size === 'lg',
    });

    const loaderIcon = (
      <div
        className={clsx(
          'w-4 h-4 border-2 border-t-transparent rounded-full animate-spin',
          variant === 'primary' && !disabled ? 'border-white' : 'border-primary-500'
        )}
        aria-hidden
      />
    );

    const hasIcon = Boolean(icon || leftIcon || rightIcon);
    const shouldShowLoaderInIconSlot = Boolean(loading && hasIcon);
    const loadingIconSlot = icon && !children ? 'icon' : leftIcon ? 'left' : rightIcon ? 'right' : null;

    const { type = 'button', ...restProps } = props;

    return (
      <button
        type={type}
        className={combinedClassName}
        disabled={disabled || loading}
        aria-busy={loading || undefined}
        {...restProps}
        ref={ref}
      >
        {loading && !hasIcon ? (
          <span className={iconWrapperClasses}>
            {loaderIcon}
          </span>
        ) : (
          <>
            {/* Icon-only button (no text) */}
            {icon && !children ? (
              <span className={iconWrapperClasses}>
                {shouldShowLoaderInIconSlot && loadingIconSlot === 'icon' ? loaderIcon : icon}
              </span>
            ) : (
              <>
                {leftIcon && (
                  <span className={iconWrapperClasses}>
                    {shouldShowLoaderInIconSlot && loadingIconSlot === 'left' ? loaderIcon : leftIcon}
                  </span>
                )}
                {children && <span>{children}</span>}
                {rightIcon && (
                  <span className={iconWrapperClasses}>
                    {shouldShowLoaderInIconSlot && loadingIconSlot === 'right' ? loaderIcon : rightIcon}
                  </span>
                )}
              </>
            )}
          </>
        )}
      </button>
    );
  }
);

Button.displayName = 'Button';

export default Button;
